import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { quoteAllOptions } from "util/manualDeliveries";
import { initialQuotesState, quotesFresh, quotesReducer } from "util/manualDeliveryBooking";
import { shipmentKey } from "util/manualDeliveryForm";

export const QUOTE_DEBOUNCE_MS = 600;

// Prices the shipment with every option, once per pause in typing. Each call gets a rising seq and quotesReducer
// applies only the newest one's answer, so an older call that finishes late can never put an old price and its quote
// token back on screen. options stays as the last answer (shown as "updating") until the next one arrives. A 401
// comes back as the expired-session error: nothing was priced or sent.
export default function useQuotes(shopId, shipment, enabled, delayMs = QUOTE_DEBOUNCE_MS) {
  const key = enabled && shopId ? shipmentKey(shipment) : null;
  const [state, dispatch] = useReducer(quotesReducer, initialQuotesState);
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);
  const mounted = useRef(true);
  // Stable, so a caller can list it in effect deps without re-quoting on every render.
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    seq.current += 1;
    const mine = seq.current;
    dispatch({ type: "request", key, seq: mine });
    if (!key) return undefined;
    const timer = setTimeout(async () => {
      let result = null;
      try {
        result = await quoteAllOptions(shopId, JSON.parse(key));
      } catch (error) {
        result = null;
      }
      if (!mounted.current) return;
      dispatch({ type: "answer", key, seq: mine, result, receivedAt: Date.now() });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [shopId, key, nonce, delayMs]);

  return {
    options: state.options,
    fresh: quotesFresh(state, key),
    loading: state.pendingKey !== null,
    error: state.error,
    receivedAt: state.receivedAt,
    answered: state.answered,
    refresh,
  };
}
